import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { X, Smartphone, Download } from 'lucide-react';
import { PWAUtils, BeforeInstallPromptEvent } from '../../utils/pwa';

// Storage keys
const DISMISSED_KEY = 'barbriq_install_dismissed';
const DISMISSED_DURATION_DAYS = 7;

// Check if user is on mobile
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

// Check if app is running as PWA (standalone mode)
const isRunningAsPWA = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

// Check if iOS device
const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & { MSStream?: unknown }).MSStream;
};

// Check if recently dismissed
const wasRecentlyDismissed = (): boolean => {
  try {
    const dismissedData = localStorage.getItem(DISMISSED_KEY);
    if (!dismissedData) return false;

    const { timestamp } = JSON.parse(dismissedData);
    const daysSinceDismissed = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
    return daysSinceDismissed < DISMISSED_DURATION_DAYS;
  } catch {
    return false;
  }
};

// Mark as dismissed
const markDismissed = (): void => {
  try {
    localStorage.setItem(
      DISMISSED_KEY,
      JSON.stringify({ timestamp: Date.now() })
    );
  } catch {
    // Storage might not be available
  }
};

interface InstallPromptProps {
  className?: string;
}

/**
 * Smart install prompt banner that appears when:
 * - User is on mobile
 * - App is not already installed
 * - User hasn't dismissed it recently
 * - BeforeInstallPrompt event is available (or iOS)
 */
export const InstallPrompt: React.FC<InstallPromptProps> = ({ className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Check if should show the prompt
  const shouldShowPrompt = useCallback((): boolean => {
    // Don't show on desktop unless explicitly testing
    if (!isMobileDevice()) return false;

    // Don't show if already installed
    if (isRunningAsPWA()) return false;

    // Don't show if recently dismissed
    if (wasRecentlyDismissed()) return false;

    return true;
  }, []);

  // Handle beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      // Show our custom prompt if conditions are met
      if (shouldShowPrompt()) {
        setIsVisible(true);
      }
    };

    // Check if we should show iOS instructions
    if (isIOS() && shouldShowPrompt()) {
      setIsVisible(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [shouldShowPrompt]);

  // Handle install button click
  const handleInstall = async () => {
    if (isIOS()) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) {
      console.warn('Install prompt not available');
      return;
    }

    setIsInstalling(true);

    try {
      // Show the install prompt
      const result = await PWAUtils.showInstallPrompt();

      if (result) {
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Install failed:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  // Handle dismiss
  const handleDismiss = () => {
    markDismissed();
    setIsVisible(false);
    setShowIOSInstructions(false);
  };

  // Handle close iOS instructions
  const handleCloseIOSInstructions = () => {
    setShowIOSInstructions(false);
  };

  // Animation variants
  const bannerVariants: Variants = {
    hidden: {
      y: 100,
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      y: 100,
      opacity: 0,
      transition: {
        duration: 0.2,
      },
    },
  };

  const modalVariants: Variants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 25,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.15,
      },
    },
  };

  return (
    <>
      {/* Main Install Banner */}
      <AnimatePresence>
        {isVisible && !showIOSInstructions && (
          <motion.div
            className={`fixed bottom-16 left-0 right-0 z-[60] sm:bottom-0 ${className}`}
            variants={bannerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="bg-white dark:bg-gray-800 shadow-lg border-t border-gray-200 dark:border-gray-700">
              <div className="max-w-lg mx-auto px-4 py-4">
                <div className="flex items-start gap-4">
                  {/* App Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                      <Smartphone className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      Install BarbrIQ
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                      Add to your home screen for quick access and offline booking
                    </p>

                    {/* Buttons */}
                    <div className="flex gap-3 mt-3">
                      <button
                        onClick={handleInstall}
                        disabled={isInstalling}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        {isInstalling ? (
                          <>
                            <svg
                              className="animate-spin h-4 w-4"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Installing...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Install
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleDismiss}
                        className="px-4 py-2.5 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white text-sm font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        Not Now
                      </button>
                    </div>
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={handleDismiss}
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* iOS Instructions Modal */}
      <AnimatePresence>
        {showIOSInstructions && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/50"
              onClick={handleCloseIOSInstructions}
            />

            {/* Modal Content */}
            <motion.div
              className="relative w-full max-w-md mx-4 mb-4 sm:mb-0"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Install BarbrIQ
                    </h3>
                    <button
                      onClick={handleCloseIOSInstructions}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Instructions */}
                <div className="px-6 py-5 space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    To install BarbrIQ on your iPhone or iPad:
                  </p>

                  <ol className="space-y-4">
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">
                        1
                      </span>
                      <div>
                        <p className="text-sm text-gray-800 dark:text-gray-200">
                          Tap the{' '}
                          <span className="font-medium">Share</span> button
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          (the square with an arrow pointing up at the bottom of Safari)
                        </p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">
                        2
                      </span>
                      <div>
                        <p className="text-sm text-gray-800 dark:text-gray-200">
                          Scroll down and tap{' '}
                          <span className="font-medium">"Add to Home Screen"</span>
                        </p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">
                        3
                      </span>
                      <div>
                        <p className="text-sm text-gray-800 dark:text-gray-200">
                          Tap{' '}
                          <span className="font-medium">"Add"</span> in the top right corner
                        </p>
                      </div>
                    </li>
                  </ol>

                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      After installation, you can launch BarbrIQ directly from your home screen
                      for a full-screen, app-like experience.
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex gap-3">
                    <button
                      onClick={handleDismiss}
                      className="flex-1 px-4 py-2.5 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white text-sm font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Maybe Later
                    </button>
                    <button
                      onClick={handleCloseIOSInstructions}
                      className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Got It
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default InstallPrompt;
